import React, { memo } from 'react';
import { css } from 'styled-components';
import Helmet from 'react-helmet';

import { useTranslation } from '@crehana/i18n';
import { useCarousel } from '@crehana/react-hooks';
import { Pagination, PrimaryButton, screens } from '@crehana/ui';
import { getImgixUrl } from '@crehana/utils';
import { ImgixParams } from '@crehana/ts-types';
import { Lazyload } from '@crehana/web';

import { useAppContext } from 'Jsx/Catalog/context/AppContext';
import type { Banner } from 'Jsx/Catalog/types';

import type { SliderHeaderProps } from './types';

const imgixOptions: ImgixParams = {
  w: 1560,
  h: 304,
  fit: 'crop',
  auto: 'format',
  q: 60,
};

const BannerSlide: React.FC<{ banner: Banner; index: number }> = memo(
  ({ banner, index }) => {
    const { t } = useTranslation();

    return (
      <a
        href={banner.link}
        className="relative block w-full h-full bg-gray-light"
      >
        {index === 0 ? (
          <>
            <Helmet>
              <link
                rel="preload"
                href={getImgixUrl(banner.banner_image, imgixOptions)}
                as="image"
                type="image/webp"
                media={`(min-width: ${screens.md})`}
              />
            </Helmet>
            <img
              src={getImgixUrl(banner.banner_image, imgixOptions)}
              alt=""
              className="absolute inset-0 object-cover w-full h-full"
              decoding="async"
            />
          </>
        ) : (
          <Lazyload observerOptions={{ rootMargin: '100%' }}>
            <img
              src={getImgixUrl(banner.banner_image, imgixOptions)}
              alt=""
              className="absolute inset-0 object-cover w-full h-full"
              decoding="async"
            />
          </Lazyload>
        )}
        <div className="relative flex flex-col justify-center h-full px-64">
          <h4
            className="text-white whitespace-normal font-h4 md:mb-8"
            style={{ color: banner.title_color }}
          >
            {banner.title}
          </h4>
          <p
            className="text-white whitespace-normal font-body3 md:mb-8 lg:mb-12"
            style={{ color: banner.sub_title_color }}
          >
            {banner.sub_title}
          </p>
          {banner.link ? (
            <PrimaryButton
              isWhite={banner.white_button}
              label={t('BANNER_BUTTON')}
              className="self-start"
              onClick={() => {
                window.location.href = banner.link;
              }}
              htmlProps={{
                style: {
                  backgroundColor: banner.top_banner_button_color,
                },
              }}
            />
          ) : null}
        </div>
      </a>
    );
  },
);

const SliderHeader: React.FC<SliderHeaderProps> = ({ className }) => {
  const { topBanners } = useAppContext();
  const totalPages = topBanners.length;
  const { currentPage, onPageChange } = useCarousel(totalPages, {
    delayStart: 15000,
  });

  if (!topBanners.length) {
    return null;
  }

  return (
    <div
      className={`relative w-full overflow-hidden ${className}`}
      style={{ height: 280 }}
      id="catalog-slider-section"
    >
      {topBanners.map((banner, index) => (
        <div
          key={String(banner.course_id).concat(index.toString())}
          className="absolute inset-y-0 w-full rounded-5 overflow-hidden"
          css={css`
            will-change: transform;
            transition: transform 300ms;
            transform: translate3d(
              calc((1280px * ${index}) + (-1280px * (${currentPage} - 1))),
              0,
              0
            );
            @media (min-width: 1680px) {
              transform: translate3d(
                calc((1560px * ${index}) + (-1560px * (${currentPage} - 1))),
                0,
                0
              );
            }
          `}
        >
          <BannerSlide banner={banner} index={index} />
        </div>
      ))}

      {totalPages > 1 ? (
        <div className="absolute inset-x-0 bottom-0 flex justify-center py-4">
          <Pagination
            currentPage={currentPage}
            onPageChange={onPageChange}
            totalPages={totalPages}
            type="dots"
            isTransparent
          />
        </div>
      ) : null}
    </div>
  );
};

export default SliderHeader;
